/**
 * Touch controls (GDD §8): a relative steering pad and the FIRE button. Steering follows the drag from wherever the
 * thumb lands in the pad: sideways steers, up boosts and down slows. Each control tracks its own finger, so steering
 * and firing work together. Auto-fire (judgment call 29) is decided by the simulation, not here.
 */
import { createSource, releaseSource, type ControlSource } from './actions.ts';

/** Drag (CSS px at 1× HUD scale) for full steer or full throttle. */
export const PAD_TRAVEL = 40;
/** Small drags ignored: a sideways drag leaves the speed alone, and a thumb at rest doesn't drift. */
const STEER_DEAD = 0.06;
const THROTTLE_DEAD = 0.18;
/** How far the drawn thumb moves inside its ring (board: 84 px ring, 36 px thumb). */
export const THUMB_TRAVEL = 22;

function axis(v: number, dead: number): number {
  const a = Math.abs(v);
  if (a <= dead) return 0;
  const s = Math.min(1, (a - dead) / (1 - dead));
  return v < 0 ? -s : s;
}

/** Steer and throttle from a drag of (dx, dy) screen px with full travel at `travel` px (screen y grows down). */
export function padAxes(dx: number, dy: number, travel: number, out: ControlSource): void {
  out.steer = axis(dx / travel, STEER_DEAD);
  out.throttle = axis(-dy / travel, THROTTLE_DEAD);
}

/** The drawn thumb's offset (design px), clamped to its ring. */
export function thumbOffset(dx: number, dy: number, scale: number, out: { x: number; y: number }): void {
  let x = dx / scale;
  let y = dy / scale;
  const d = Math.hypot(x, y);
  if (d > THUMB_TRAVEL) {
    x *= THUMB_TRAVEL / d;
    y *= THUMB_TRAVEL / d;
  }
  out.x = x;
  out.y = y;
}

export interface TouchElements {
  pad: HTMLElement;
  thumb: HTMLElement;
  fire: HTMLElement;
}

export interface TouchControls {
  readonly source: ControlSource;
  /** Re-binds after the HUD rebuilds its layout. */
  bind(el: TouchElements | null): void;
  release(): void;
  dispose(): void;
}

/** `scale` is the controls' current HUD scale (drags and the drawn thumb follow it). */
export function createTouchControls(scale: () => number): TouchControls {
  const source = createSource();
  const pad = { id: -1, ox: 0, oy: 0 };
  const fire = { id: -1 };
  const off = { x: 0, y: 0 };
  let el: TouchElements | null = null;

  function padDown(e: PointerEvent): void {
    if (pad.id !== -1 || !el) return;
    e.preventDefault();
    pad.id = e.pointerId;
    pad.ox = e.clientX;
    pad.oy = e.clientY;
    el.pad.setPointerCapture?.(e.pointerId);
    el.pad.classList.add('hw-active');
    padMove(e);
  }
  function padMove(e: PointerEvent): void {
    if (e.pointerId !== pad.id || !el) return;
    const s = scale();
    const dx = e.clientX - pad.ox;
    const dy = e.clientY - pad.oy;
    padAxes(dx, dy, PAD_TRAVEL * s, source);
    thumbOffset(dx, dy, s, off);
    el.thumb.style.transform = `translate(${off.x.toFixed(1)}px, ${off.y.toFixed(1)}px)`;
  }
  function padUp(e: PointerEvent): void {
    if (e.pointerId !== pad.id) return;
    pad.id = -1;
    source.steer = 0;
    source.throttle = 0;
    if (el) {
      el.pad.classList.remove('hw-active');
      el.thumb.style.transform = '';
    }
  }
  function fireDown(e: PointerEvent): void {
    if (fire.id !== -1 || !el) return;
    e.preventDefault();
    fire.id = e.pointerId;
    el.fire.setPointerCapture?.(e.pointerId);
    el.fire.classList.add('hw-active');
    source.fire = true;
  }
  function fireUp(e: PointerEvent): void {
    if (e.pointerId !== fire.id) return;
    fire.id = -1;
    source.fire = false;
    el?.fire.classList.remove('hw-active');
  }
  const noMenu = (e: Event): void => e.preventDefault();

  function listen(on: boolean): void {
    if (!el) return;
    const m = on ? 'addEventListener' : 'removeEventListener';
    el.pad[m]('pointerdown', padDown as EventListener);
    el.pad[m]('pointermove', padMove as EventListener);
    el.pad[m]('pointerup', padUp as EventListener);
    el.pad[m]('pointercancel', padUp as EventListener);
    el.pad[m]('lostpointercapture', padUp as EventListener);
    el.fire[m]('pointerdown', fireDown as EventListener);
    el.fire[m]('pointerup', fireUp as EventListener);
    el.fire[m]('pointercancel', fireUp as EventListener);
    el.fire[m]('lostpointercapture', fireUp as EventListener);
    el.pad[m]('contextmenu', noMenu);
    el.fire[m]('contextmenu', noMenu);
  }

  const api: TouchControls = {
    source,
    bind(next) {
      api.release();
      listen(false);
      el = next;
      listen(true);
    },
    release() {
      pad.id = -1;
      fire.id = -1;
      releaseSource(source);
      if (el) {
        el.pad.classList.remove('hw-active');
        el.fire.classList.remove('hw-active');
        el.thumb.style.transform = '';
      }
    },
    dispose() {
      api.bind(null);
    },
  };
  return api;
}
